/**
 * The HTTP client.
 *
 * One place that knows about the API's origin, its error envelope, the CSRF
 * token and the session cookie — so no component ever handles a raw `fetch`.
 *
 * ## Where the API lives
 *
 * `VITE_API_BASE_URL` selects between two deployment shapes:
 *
 * - **unset** — the API is same-origin under `/api`. This is local development
 *   (Vite proxies `/api` to :8000) and any single-domain deployment.
 * - **set** — the API is on its own host, e.g. `https://api.mirondo.cz`
 *   while the app is on `https://app.mirondo.cz`.
 *
 * Vite inlines the value at build time, so it is a Docker build argument rather
 * than a runtime environment variable (see web/Dockerfile).
 *
 * ## Why cross-origin is safe here
 *
 * Both hosts share one registrable domain. Browsers judge `SameSite` by site
 * (`mirondo.cz`), not by origin, so the session cookie is still same-site:
 * `SameSite=Lax` keeps working and third-party cookie blocking does not apply.
 * What cross-origin does cost is a CORS preflight on writes, and
 * `credentials: 'include'` instead of `'same-origin'`.
 */

const configuredApiOrigin = (import.meta.env.VITE_API_BASE_URL ?? '').replace(
  /\/+$/,
  '',
);

/** Absolute when the API is on its own host, otherwise a relative prefix. */
export const API_PREFIX = `${configuredApiOrigin}/api/v1`;

const isCrossOrigin =
  configuredApiOrigin !== '' &&
  new URL(configuredApiOrigin, window.location.origin).origin !==
    window.location.origin;

/**
 * Cookies must ride along. `'include'` only where it is actually needed —
 * `'same-origin'` otherwise, so a same-domain deployment keeps the stricter
 * default rather than opting into cross-origin credentials it never uses.
 */
const CREDENTIALS: RequestCredentials = isCrossOrigin ? 'include' : 'same-origin';

/**
 * The bearer token, when the app is running inside the desktop shell.
 *
 * Google refuses OAuth in an embedded web view, so the shell cannot sign in
 * the way a browser does: it opens the system browser, receives the token back
 * on the `mirondo://` scheme, and hands it here. From then on this behaves
 * like the macOS client — a token the app attaches deliberately, which is why
 * CSRF does not apply to it and cannot be forged by another origin.
 *
 * In a browser there is no shell and no token, and everything below is inert.
 */
const SHELL_TOKEN_KEY = 'mirondo.shell_token';

function shellToken(): string | null {
  try {
    return window.localStorage.getItem(SHELL_TOKEN_KEY);
  } catch {
    // A locked-down browser can throw on storage access; a missing token is
    // simply the browser case.
    return null;
  }
}

/** Called by the desktop shell once it has a token from the sign-in round trip. */
export function setShellToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(SHELL_TOKEN_KEY, token);
    else window.localStorage.removeItem(SHELL_TOKEN_KEY);
  } catch {
    // Nothing to do: without storage the shell simply signs in again.
  }
}

const CSRF_COOKIE = 'mirondo_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** The backend's error envelope: `{"error": {"code", "message"}}`. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

/**
 * A failed request, carrying the backend's stable error `code`.
 *
 * Callers switch on `code`, never on the message — the message is for humans
 * and may be reworded at any time.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Whether this means "not signed in", which the app handles globally. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Whether retrying could plausibly help. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

// ---------------------------------------------------------------------------
// CSRF
// ---------------------------------------------------------------------------

/**
 * The double-submit token, held in memory.
 *
 * Reading it from `document.cookie` only works when the app and the API share a
 * host. With the API on its own subdomain the cookie is host-only to *that*
 * host and this document cannot see it — and the fix is emphatically **not** to
 * widen the cookie to `Domain=.mirondo.cz`, which would broadcast the session to
 * every other subdomain of the parent domain.
 *
 * So the token comes from the response body of `GET /auth/csrf` instead. The
 * browser still sends the cookie to the API, so the server's comparison is
 * unchanged; only this side stops needing to read it.
 */
let csrfToken: string | null = null;

async function ensureCsrfToken(): Promise<string | null> {
  if (csrfToken) return csrfToken;

  // Same-origin deployments already have it, and reading the cookie avoids a
  // request.
  const fromCookie = readCookie(CSRF_COOKIE);
  if (fromCookie) {
    csrfToken = fromCookie;
    return csrfToken;
  }

  try {
    const response = await fetch(`${API_PREFIX}/auth/csrf`, {
      credentials: CREDENTIALS,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { csrf_token?: string };
    csrfToken = body.csrf_token ?? null;
  } catch {
    // Offline, or not signed in. The request that needed the token will fail on
    // its own terms and report something more useful than "no CSRF token".
    return null;
  }
  return csrfToken;
}

/** Drop the cached token — on logout, or after the server rejects it. */
export function clearCsrfToken(): void {
  csrfToken = null;
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Sent as multipart instead of JSON. */
  formData?: FormData;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  // A bearer token carries its own proof; CSRF guards cookies, which this
  // request does not rely on.
  const needsCsrf = UNSAFE_METHODS.has(method) && shellToken() === null;

  const response = await send(
    path,
    options,
    needsCsrf ? await ensureCsrfToken() : null,
  );

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    const error = await toApiError(response);

    // A rejected CSRF token usually means ours went stale (the session was
    // renewed, or the server restarted). Fetch a fresh one and retry exactly
    // once — retrying further would loop against a genuine rejection.
    if (needsCsrf && error.code === 'CSRF_TOKEN_INVALID') {
      clearCsrfToken();
      const retry = await send(path, options, await ensureCsrfToken());
      if (retry.status === 204) return undefined as T;
      if (!retry.ok) throw await toApiError(retry);
      return parse<T>(retry);
    }

    throw error;
  }

  return parse<T>(response);
}

function send(
  path: string,
  options: RequestOptions,
  csrf: string | null,
): Promise<Response> {
  const headers = new Headers();
  let body: BodyInit | undefined;

  if (options.formData) {
    // Deliberately no Content-Type: the browser must set the multipart boundary
    // itself, and overriding it produces an unparseable request.
    body = options.formData;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  if (csrf) headers.set(CSRF_HEADER, csrf);

  const token = shellToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  return fetch(`${API_PREFIX}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body,
    credentials: CREDENTIALS,
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

async function parse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.blob()) as T;
  }
  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN_ERROR';
  let message = `Request failed with status ${response.status}.`;
  let details: Record<string, unknown> | undefined;

  try {
    const payload = (await response.json()) as Partial<ApiErrorBody>;
    if (payload.error) {
      code = payload.error.code ?? code;
      message = payload.error.message ?? message;
      details = payload.error.details;
    }
  } catch {
    // A non-JSON error body (a proxy timeout page, say) is not worth failing
    // over — the status code still tells us what happened.
  }

  return new ApiError(response.status, code, message, details);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', formData }),
};

/** Build a query string, dropping empty values so URLs stay readable. */
export function query(
  params: Record<string, string | number | boolean | undefined | null>,
) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const serialised = search.toString();
  return serialised ? `?${serialised}` : '';
}
